import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { getAuthService } from '../../services/authService';
import { OpenAPI, AbpApplicationConfigurationService } from '../../services/wealthlane-identity-services';
import { getFilteredPermissionData } from '../../utils';

// --- Phase State Machine ---
export type AuthPhase = 'idle' | 'oidc-loading' | 'abp-loading' | 'ready' | 'unauthenticated' | 'error';

interface AuthState {
  phase: AuthPhase;
  token: string | null;
  isAuthenticated: boolean | null;
  user: any;
}

const initialState: AuthState = {
  phase: 'idle',
  token: null,
  isAuthenticated: null,
  user: null,
};

// --- Async Thunks ---

export const bootstrapAuth = createAsyncThunk(
  'auth/bootstrap',
  async (_, { rejectWithValue }) => {
    const authService = getAuthService();
    const user = await authService.getUser();
    if (!user) return rejectWithValue('no-user');
    return {
      token: authService.getToken(),
      user,
      isAuthenticated: authService.isAuthenticated(),
    };
  }
);

export const fetchPermissions = createAsyncThunk(
  'auth/fetchPermissions',
  async (_, { getState, rejectWithValue }) => {
    const { auth } = getState() as RootState;
    if (!auth.token) return rejectWithValue('no-token');

    OpenAPI.BASE = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
    OpenAPI.TOKEN = auth.token;

    let lastError: any;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await AbpApplicationConfigurationService
          .getApiIdentityApiAbpApplicationConfiguration();
        const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
          response?.auth?.grantedPolicies
        );
        return {
          currentUserAuthList: response ?? {},
          currentUserRoutesList: Object.keys(nonFieldLevelPermissionsGroups),
        };
      } catch (err) {
        lastError = err;
        if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    return rejectWithValue(lastError);
  }
);

// --- Slice ---
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      {
        payload: { isAuthenticated, token, user },
      }: PayloadAction<{ isAuthenticated: boolean; token: string; user: object }>
    ) => {
      state.isAuthenticated = isAuthenticated;
      state.token = token;
      state.user = user;
    },
    setPhase(state, action: PayloadAction<AuthPhase>) {
      state.phase = action.payload;
    },
    tokenRefreshed(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    loggedOut() {
      return { ...initialState, phase: 'unauthenticated' as AuthPhase };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapAuth.pending, (state) => { state.phase = 'oidc-loading'; })
      .addCase(bootstrapAuth.fulfilled, (state, action) => {
        const { token, user, isAuthenticated } = action.payload as {
          token: string;
          user: any;
          isAuthenticated: boolean;
        };
        state.token = token;
        state.user = user;
        state.isAuthenticated = isAuthenticated;
        state.phase = 'abp-loading';
      })
      .addCase(bootstrapAuth.rejected, (state) => { state.phase = 'unauthenticated'; })
      .addCase(fetchPermissions.pending, (state) => { state.phase = 'abp-loading'; })
      .addCase(fetchPermissions.fulfilled, (state) => {
        state.phase = 'ready';
      })
      .addCase(fetchPermissions.rejected, (state) => { state.phase = 'error'; });
  },
});

export const { setCredentials, setPhase, tokenRefreshed, loggedOut } = authSlice.actions;
export default authSlice.reducer;

// --- Selectors ---
export const selectToken = (state: RootState): string | null => state.auth.token;
export const selectAuthPhase = (state: RootState): AuthPhase => state.auth.phase;
export const selectIsAuthenticated = (state: RootState): boolean | null => state.auth.isAuthenticated;
export const selectUser = (state: RootState): any => state.auth.user;
